import { Link } from '@/components/atoms/Link';
import { Skeleton } from '@/components/atoms/Skeleton';
import Badge, { BadgeVariants } from '@/components/molecules/Badge';
import {
  ArticleCard,
  ArticleCardSkeleton,
} from '@/components/organisms/ArticleCard';
import { useArticles } from '@/hooks/queries/articles/useArticles';
import useWindowSize from '@/hooks/useWindowSize';
import { CategoryPreview } from '@/services/wordpress/categories';
import { BlogPreview } from '@/types/blog';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

interface RelatedArticlesAndCategoriesProps {
  categories: CategoryPreview[];
  articleIdsToExclude: string[];
  onFetchedArticles: (articles: BlogPreview[]) => void;
}

export const RelatedArticlesAndCategories = ({
  categories,
  articleIdsToExclude,
  onFetchedArticles,
}: RelatedArticlesAndCategoriesProps) => {
  const pageSize = 4;

  const { inView, ref } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const { isDesktop } = useWindowSize();

  const {
    data: recentArticlesData,
    isFetching: isRecentArticlesLoading,
    isError,
    isFetched,
  } = useArticles({
    queryKey: ['recentArticles'],
    limit: pageSize,
    exclude: articleIdsToExclude,
    enabled: inView,
  });

  useEffect(() => {
    if (isFetched && recentArticlesData) {
      onFetchedArticles(recentArticlesData);
    }
  }, [onFetchedArticles, recentArticlesData, isFetched]);

  return (
    <div className="flex w-full flex-col" ref={isFetched ? undefined : ref}>
      {isError && (
        <p className="text-center text-red-500">
          There was a problem fetching related articles. Please reload the page
          and try again.
        </p>
      )}

      {isRecentArticlesLoading && (
        <div className="flex flex-col gap-m">
          <Skeleton style={{ width: 191, height: 26 }} />
          <div className="grid w-full grid-cols-1 gap-4">
            {Array.from({ length: pageSize }).map((_, index) => (
              <ArticleCardSkeleton key={index} />
            ))}
          </div>
        </div>
      )}

      {recentArticlesData && !isRecentArticlesLoading ? (
        <div className="mb-xl flex flex-col gap-xl l:gap-l">
          <h3 className="text-h3SemiBold">You might also like</h3>
          <div className="flex w-full flex-col items-center gap-l md:grid md:grid-cols-2 l:flex l:flex-col">
            {recentArticlesData.map((post) => (
              <div key={post.id}>
                <ArticleCard
                  article={post}
                  size={isDesktop ? 'small' : 'medium'}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className="-mx-l -mb-xl flex flex-col gap-l bg-white p-l pb-[40px] l:m-0 l:bg-neutral-100 l:pb-l">
        <h4 className="text-h4SemiBold">Categories</h4>
        <div className="mb-s flex flex-row flex-wrap gap-l">
          {categories.map((category) => (
            <Link href={`/news/category/${category.slug}`} key={category.id}>
              <Badge label={category.name} variant={BadgeVariants.Blue} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
